import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { Col, Row } from 'react-bootstrap'
import { Title, Card, Loading, Dropdown } from 'src/components'

import {
  actionTypes,
  downloadProductsExcel,
  downloadTransactionsExcel,
  downloadTransactionsPDF,
  findByCode,
  getClientThresholdsStatementAccount,
} from 'src/actions/clients.actions'

import { actionTypes as segmentedActions } from 'src/actions/report.actions'

import { selectClientByCode } from 'src/selectors/clients.selector'
import { loadingSelector } from 'src/selectors/loading.selector'
import { handlerError, hasErrors } from 'src/selectors/error.selector'
import { haveAnyValue } from 'src/utils/utilitiesV2'
import { showAlert } from 'src/actions/alert.actions'

import ClientTransactions, {
  IClientTransactionsParams,
} from 'src/content/Clients/ClientTransactions/ClientTransactions'
import ClientTransactionsSummary from 'src/content/Clients/ClientTransactions/ClientTransactionsSummary'
import { faFileExcel, faFilePdf } from '@fortawesome/free-solid-svg-icons'
import { getThresholds } from 'src/actions/thresholds.action'

import { ThresholdEnum } from 'src/content/Threshold/ThresholdEnum'
import { selectThresholds } from 'src/selectors/thresholds.selector'
import { getSegmentationReportOptExcel } from 'src/actions/report.actions'
import { segmentationReportTypeEnum } from 'src/enums/reportsEnum'
import { exportDTE, exportDTEProducts } from 'src/actions/resolutions.actions'

interface Flags {
  getClient: boolean
  initTransactions: boolean
}

export const ClientTransactionPage = ({ match }) => {
  const dispatch = useDispatch()

  const { code } = match.params

  const loadingGetClient = useSelector(state =>
    loadingSelector([actionTypes.GET_CLIENT_BY_CODE])(state),
  )
  const errorGetClient = useSelector(state =>
    hasErrors([actionTypes.GET_CLIENT_BY_CODE])(state),
  )
  const client = useSelector(selectClientByCode)

  const loadingDownload: boolean = useSelector(state =>
    loadingSelector([
      actionTypes.DOWNLOAD_EXCEL_TRANSACTIONS,
      actionTypes.GET_EXCEL_PRODUCTS,
      segmentedActions.GET_SEGMENTATION_REPORT_EXCEL,
      actionTypes.DOWNLOAD_PDF_TRANSACTIONS,
    ])(state),
  )

  const thresholds: IThreshold[] = useSelector(selectThresholds)

  const [flags, setFlags] = useState<Flags>({
    getClient: false,
    initTransactions: false,
  })

  const [params, setParams] = useState<Partial<IClientTransactionsParams>>({})

  useEffect(() => {
    if (!haveAnyValue(code)) return
    dispatch(findByCode(code))
    dispatch(getThresholds(ThresholdEnum.CLIENTS))
  }, [code])

  useEffect(() => {
    if (loadingGetClient) setFlags({ ...flags, getClient: true, initTransactions: false })
    else if (flags.getClient) {
      if (errorGetClient) {
        setFlags({ ...flags, getClient: false })
        dispatch(showAlert({ ...handlerError(errorGetClient.message) }))
      } else {
        setFlags({ ...flags, getClient: false, initTransactions: true })
      }
    }
  }, [loadingGetClient])

  const setUpThreshold = () => {
    if (!haveAnyValue(client.id)) return
    dispatch(getClientThresholdsStatementAccount(client.id, params))
  }

  useEffect(() => {
    setUpThreshold()
  }, [(params?.start || 0) + (params?.end || 0), client.id])

  const onDownloadReport = (withPayments?: boolean) => {
    dispatch(downloadTransactionsExcel(client.id, { ...params, withPayments }))
  }

  /**
   * Downloads the transactions report in PDF format for the current client.
   * Dispatches the `downloadTransactionsPDF` action with the client ID and parameters.
   */
  const onDownloadReportPDF = () => {
    dispatch(downloadTransactionsPDF(client.id, { ...params, size: null }))
  }

  const onDownloadItems = () => {
    dispatch(
      downloadProductsExcel(client.id, {
        start: params.start,
        end: params.end,
      }),
    )
  }

  const onDownloadItemsInvoiced = (items: boolean) => {
    if (!haveAnyValue(params.start) || !haveAnyValue(params.end)) {
      dispatch(showAlert(handlerError('Es necesario seleccionar un rango de fechas.')))
      return
    }

    const request = {
      start: params.start,
      end: params.end,
      categories: '',
      client: client.id.toString(),
      extraFields: true,
    }
    if (items) dispatch(exportDTEProducts(request))
    else dispatch(exportDTE(request))
  }

  const downloadActions = (
    <Dropdown
      loading={loadingDownload}
      items={[
        {
          title: 'Descargar reporte de transacciones',
          icon: faFileExcel,
          color: 'green',
          action: onDownloadReport,
        },
        {
          title: 'Descargar reporte detallado con pagos',
          icon: faFileExcel,
          color: 'green',
          action: () => onDownloadReport(true),
        },
        {
          title: 'Descargar ítems',
          icon: faFileExcel,
          color: 'green',
          action: () => onDownloadItems(),
        },
        {
          title: 'Descargar ítems facturados',
          icon: faFileExcel,
          color: 'green',
          action: () => onDownloadItemsInvoiced(true),
        },
        {
          title: 'Descargar reporte en PDF',
          icon: faFilePdf,
          color: 'green',
          action: () => onDownloadReportPDF(),
        },
      ]}
    />
  )

  return (
    <div>
      <Title title={`Estado de cuenta: ${code} ${client?.companyName ?? ''}`} />
      <Row>
        <Col xl={12}>
          <ClientTransactionsSummary />
        </Col>
        <Col xl={12} lg={12} md={12} sm={12}>
          <Card title={'Transacciones'} white button={downloadActions}>
            {loadingGetClient ? (
              <Loading show />
            ) : flags.initTransactions ? (
              <ClientTransactions
                clientId={client.id}
                balancePermission
                getParams={params => setParams(params)}
                thresholds={thresholds}
              />
            ) : (
              <div />
            )}
          </Card>
        </Col>
      </Row>
    </div>
  )
}
